import React, { useRef, useEffect, useState, useLayoutEffect } from 'react';
import type { RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import BlackCheckMarkIcon from 'app/assets/svgIcons/BlackCheckMarkIcon';
import { useAnchorRelativeOverlayPosition } from 'app/components/SlideOutPanel/hooks/useAnchorRelativeOverlayPosition';
import { selectSortSelections } from '../../slice/selectors';
import styles from './ColumnSortMenu.module.scss';

export interface SortMenuOption {
  key: string;
  label: string;
  group: string;
  order: number;
}

interface ColumnSortMenuProps {
  columnKey: string;
  options: SortMenuOption[];
  selectedOptions: Set<string>;
  onOptionToggle: (columnKey: string, optionKey: string) => void;
  isOpen: boolean;
  onClose: () => void;
  anchorElement: HTMLElement | null;
  /**
   * `bodyPortal`: `position: fixed` via portal to `document.body` (Milestones grid).
   * `overlayRootAbsolute`: no portal; `position: absolute` inside the given
   * `overlayRootRef` (sibling of a scroll/overflow area) so the menu is not
   * clipped by `overflow: auto` (e.g. change log). Uses `useAnchorRelativeOverlayPosition`.
   */
  positionMode?: 'bodyPortal' | 'overlayRootAbsolute';
  /** Required when `positionMode === 'overlayRootAbsolute'`: `position: relative` root that wraps the table + this menu. */
  overlayRootRef?: RefObject<HTMLElement | null>;
}

interface MenuPosition {
  top: number;
  left: number;
}

export const ColumnSortMenu: React.FC<ColumnSortMenuProps> = ({
  columnKey,
  options,
  selectedOptions,
  onOptionToggle,
  isOpen,
  onClose,
  anchorElement,
  positionMode = 'bodyPortal',
  overlayRootRef
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const anchorForOverlayRef = useRef<HTMLElement | null>(null);
  const [position, setPosition] = useState<MenuPosition>({ top: 0, left: 0 });

  // Get pin state to trigger position recalculation when columns reorder
  const sortSelections = useSelector(selectSortSelections);
  const pinState = `${sortSelections.sharedPin}-${sortSelections.sharedPinColumn}`;

  useLayoutEffect(() => {
    anchorForOverlayRef.current = anchorElement;
  }, [anchorElement]);

  const overlayPositionEnabled =
    positionMode === 'overlayRootAbsolute' && isOpen && !!overlayRootRef;

  useAnchorRelativeOverlayPosition({
    anchorRef: anchorForOverlayRef,
    overlayRootRef: overlayRootRef ?? menuRef,
    overlayRef: menuRef,
    layoutKey: `${isOpen ? 1 : 0}-${columnKey}-${anchorElement ? 1 : 0}`,
    margin: 8,
    gapY: 8,
    enabled: overlayPositionEnabled
  });

  // Calculate menu position based on anchor element (portal + fixed only)
  useEffect(() => {
    if (positionMode !== 'bodyPortal' || !isOpen || !anchorElement) return;

    const updatePosition = () => {
      const rect = anchorElement.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 8, // 8px gap below the icon
        left: rect.left
      });
    };

    // Use requestAnimationFrame to ensure DOM has updated after column reorder
    const rafId = requestAnimationFrame(() => {
      updatePosition();
    });

    // Update position on scroll or resize
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [positionMode, isOpen, anchorElement, pinState]); // pinState: recalc when columns reorder

  // Close menu when clicking outside (use capture so we run before stopPropagation in table/scroll)
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Don't close if clicking inside the menu
      if (menuRef.current && menuRef.current.contains(target)) {
        return;
      }

      // Don't close if clicking the anchor element (sort icon)
      if (anchorElement && anchorElement.contains(target)) {
        return;
      }

      onClose();
    };

    document.addEventListener('mousedown', handleClickOutside, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [isOpen, onClose, anchorElement]);

  if (!isOpen) return null;

  // Sort options by order field
  const sortedOptions = [...options].sort((a, b) => a.order - b.order);

  const handleOptionClick = (optionKey: string) => {
    onOptionToggle(columnKey, optionKey);
  };

  const menuClassName =
    positionMode === 'overlayRootAbsolute'
      ? `${styles.sortMenu} ${styles.sortMenuOverlayRoot}`
      : styles.sortMenu;

  // `bodyPortal`: fixed + portaled. `overlayRootAbsolute`: abs in `overlayRootRef` (not clipped by overflow on inner scroll)
  const menuContent = (
    <div
      ref={menuRef}
      className={menuClassName}
      style={
        positionMode === 'bodyPortal'
          ? {
              position: 'fixed',
              top: `${position.top}px`,
              left: `${position.left}px`
            }
          : undefined
      }
    >
      {sortedOptions.map(option => {
        const isSelected = selectedOptions.has(option.key);

        return (
          <div
            key={option.key}
            className={styles.menuItem}
            onClick={() => handleOptionClick(option.key)}
          >
            <span className={styles.menuItemLabel}>{option.label}</span>
            {isSelected && (
              <BlackCheckMarkIcon
                width={18}
                height={16}
                className={styles.checkmark}
              />
            )}
          </div>
        );
      })}
    </div>
  );

  if (positionMode === 'overlayRootAbsolute') {
    return menuContent;
  }

  return createPortal(menuContent, document.body);
};

export default ColumnSortMenu;
